import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { getAppTheme } from '../theme/tokens.js';

const ThemeContext = createContext();

const isSplashActive = () => {
  if (typeof window !== 'undefined' && window.__unmaSkipSplash) return false;
  if (typeof window !== 'undefined' && window.__unmaSplashActive === false) return false;
  return typeof document !== 'undefined' && Boolean(document.getElementById('boot-splash'));
};

// Helper function to get initial theme
const getInitialTheme = () => {
  try {
    if (typeof window !== 'undefined' && window.__unmaBootTheme === 'dark') return true;
    if (typeof window !== 'undefined' && window.__unmaBootTheme === 'light') return false;
    // Check if user has a theme preference in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark' || savedTheme === 'light') {
        return savedTheme === 'dark';
      }
    }
    // Check if user's system prefers dark mode
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  } catch (error) {
    console.warn('Error accessing theme preferences:', error);
  }
  return false; // Default to light mode
};

const applyDocumentTheme = (isDarkMode) => {
  try {
    const theme = isDarkMode ? 'dark' : 'light';
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('theme', theme);
    }
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle('dark', isDarkMode);
    body.classList.toggle('dark', isDarkMode);
    root.dataset.theme = theme;
    root.style.colorScheme = theme;
    // Prefer CSS variables so color transitions remain smooth.
    root.style.backgroundColor = '';
    body.style.backgroundColor = '';

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDarkMode ? '#0f172a' : '#f8fafc');
    }

    const metaColorScheme = document.querySelector('meta[name="color-scheme"]');
    if (metaColorScheme) {
      metaColorScheme.setAttribute('content', theme);
    }
  } catch (error) {
    console.warn('Error updating theme:', error);
  }
};

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 767px)').matches;

const preserveMobileScroll = (apply) => {
  if (!isMobileViewport() || typeof window === 'undefined') {
    apply();
    return;
  }

  const windowY = window.scrollY;
  const mainEl = document.querySelector('.ios-app-main');
  const mainY = mainEl ? mainEl.scrollTop : 0;
  const root = document.documentElement;

  root.classList.add('theme-switching-mobile');
  apply();

  const restore = () => {
    window.scrollTo(0, windowY);
    if (mainEl) mainEl.scrollTop = mainY;
  };

  restore();
  requestAnimationFrame(() => {
    restore();
    requestAnimationFrame(() => {
      restore();
      root.classList.remove('theme-switching-mobile');
    });
  });
};

const runThemeUpdate = (updater) => {
  const apply = () => {
    flushSync(updater);
  };

  // Mobile: skip View Transitions — they snapshot/crossfade the page and cause
  // flicker, layout jump, and scroll drift. Color CSS transitions handle the rest.
  if (isMobileViewport()) {
    preserveMobileScroll(apply);
    return;
  }

  if (typeof document !== 'undefined' && typeof document.startViewTransition === 'function') {
    document.startViewTransition(apply);
    return;
  }

  apply();
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);
  const [splashReady, setSplashReady] = useState(() => !isSplashActive());

  useEffect(() => {
    if (splashReady) return undefined;
    const handleSplashDone = () => {
      window.__unmaSplashActive = false;
      setSplashReady(true);
    };
    window.addEventListener('unma-splash-done', handleSplashDone);
    // Splash may already have been removed before this effect runs.
    if (!isSplashActive()) handleSplashDone();
    return () => window.removeEventListener('unma-splash-done', handleSplashDone);
  }, [splashReady]);

  // Apply theme before paint when splash is not blocking (desktop / already dismissed).
  useLayoutEffect(() => {
    if (!splashReady && !window.__unmaSkipSplash) return;
    applyDocumentTheme(isDarkMode);
    // Enable CSS theme transitions only after splash is fully gone.
    window.requestAnimationFrame(() => {
      if (document.getElementById('boot-splash')) return;
      document.documentElement.classList.add('theme-ready');
    });
  }, [isDarkMode, splashReady]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if user hasn't set a preference
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        runThemeUpdate(() => setIsDarkMode(e.matches));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    runThemeUpdate(() => setIsDarkMode((prev) => !prev));
  };

  const setTheme = (theme) => {
    runThemeUpdate(() => setIsDarkMode(theme === 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ 
      isDarkMode, 
      toggleTheme, 
      setTheme,
      theme: isDarkMode ? 'dark' : 'light',
      colors: getAppTheme(isDarkMode),
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
